/* ------------------------------------------------------------------
  File: InfoCard.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable structured information card for the Accend Holdings website.
//   - Supports icon, eyebrow, title, description, action, and card styling options.
//   - Uses BaseCard as the foundational shell.
//   - Uses the acc-info-card namespace to avoid conflicts with Bootstrap.
//   - Suitable for services, values, sectors, benefits, capabilities, and subsidiaries.

/**
 * InfoCard
 *
 * A reusable information card component for common content blocks.
 *
 * @param {React.ReactNode} icon
 * Optional icon displayed above the title.
 *
 * @param {string} eyebrow
 * Optional small label displayed above the title.
 *
 * @param {string} title
 * Main card title.
 *
 * @param {string} description
 * Supporting card description.
 *
 * @param {React.ReactNode} action
 * Optional action element such as a Button or link.
 *
 * @param {"default" | "muted" | "soft" | "inverse"} [variant="default"]
 * Controls the BaseCard surface style.
 *
 * @param {"none" | "sm" | "md" | "lg"} [padding="md"]
 * Controls BaseCard internal spacing.
 *
 * @param {boolean} [hoverable=false]
 * Enables BaseCard hover behavior.
 *
 * @param {boolean} [bordered=true]
 * Controls whether the BaseCard has a border.
 *
 * @param {boolean} [fullHeight=false]
 * Makes the card stretch to the full height of its parent.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import BaseCard from "./BaseCard";
import "./InfoCard.css";

const InfoCard = ({
  icon,
  eyebrow,
  title,
  description,
  action,
  variant = "default",
  padding = "md",
  hoverable = false,
  bordered = true,
  fullHeight = false,
  className = "",
  ...props
}) => {
  const infoCardClasses = ["acc-info-card", className]
    .filter(Boolean)
    .join(" ");

  return (
    <BaseCard
      as="article"
      variant={variant}
      padding={padding}
      hoverable={hoverable}
      bordered={bordered}
      fullHeight={fullHeight}
      className={infoCardClasses}
      {...props}
    >
      <div className="acc-info-card__inner l-stack l-stack--sm">
        {(icon || eyebrow) && (
          <div className="acc-info-card__top l-stack l-stack--xs">
            {icon && (
              <span className="acc-info-card__icon" aria-hidden="true">
                {icon}
              </span>
            )}

            {eyebrow && (
              <span className="acc-info-card__eyebrow">{eyebrow}</span>
            )}
          </div>
        )}

        <div className="acc-info-card__content l-stack l-stack--xs">
          {title && <h3 className="acc-info-card__title">{title}</h3>}

          {description && (
            <p className="acc-info-card__description">{description}</p>
          )}
        </div>

        {action && <div className="acc-info-card__action">{action}</div>}
      </div>
    </BaseCard>
  );
};

export default InfoCard;